<script setup lang="ts">
import { defineProps, defineEmits, computed, readonly } from 'vue';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { useStore } from 'vuex';
import { RequestStatus } from '@/types/request.types';

import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Avatar from 'primevue/avatar';

import TeamUserProfileStatusTag from '@/pages/team/components/TeamUserProfileStatusTag.vue';
import TeamUserActionsMenu from '@/pages/team/components/TeamUserActionsMenu.vue';
import TeamResendInviteButton from '@/pages/team/components/TeamResendInviteButton.vue';
import TeamUserInviteButton from '@/pages/team/components/TeamUserInviteButton.vue';

// -----
// Props & Emits
// -----

defineProps<{
  value: UserResponseDto[];
}>();

const emit = defineEmits<{
  rowSelect: [user: UserResponseDto];
  deleteUser: [user: UserResponseDto];
}>();

const handleRowSelect = (event: { data: UserResponseDto }) => {
  emit('rowSelect', event.data);
};

// -----
// Setup
// -----
const store = useStore();

// -----
// Computed Properties
// -----

const requestStatuses = readonly({
  getUsersForBusiness: computed<RequestStatus>(() =>
    store.getters.requestStatus('GET_USERS_FOR_BUSINESS'),
  ),
});

// -----
// Event Handlers
// -----

const handleDeleteUser = (user: UserResponseDto) => {
  emit('deleteUser', user);
};
</script>

<template>
  <div class="users-table">
    <DataTable
      :value="value"
      :loading="requestStatuses.getUsersForBusiness.isPending"
      selectionMode="single"
      dataKey="id"
      @rowSelect="handleRowSelect"
      :pt="{
        bodyCell: {
          style: 'padding: 0.25rem 0.75rem !important;',
        },
      }"
    >
      <Column
        field="fullName"
        :header="$t('teamPage.columnHeaders.name')"
        class="name-column"
      >
        <template #body="slotProps">
          <div class="user-name">
            <Avatar
              :label="slotProps.data.firstName.charAt(0).toUpperCase()"
              class="user-avatar"
              shape="circle"
              style="background-color: #ede9fe; color: #7c3aed"
            />
            <span class="user-name-text">{{ slotProps.data.fullName }}</span>
          </div>
        </template>
      </Column>
      <Column
        field="role"
        :header="$t('teamPage.columnHeaders.userRole')"
        class="role-column"
      >
        <template #header>
          <span
            v-tooltip.hover="$t('teamPage.roleInfo')"
            class="pi pi-info-circle tooltip"
            aria-label="Role info"
          />
        </template>
        <template #body="slotProps">
          {{ slotProps.data.role || $t('teamPage.roles.admin') }}
        </template>
      </Column>
      <Column
        field="email"
        :header="$t('teamPage.columnHeaders.email')"
        class="email-column"
      ></Column>
      <Column
        field="profileCompleted"
        :header="$t('teamPage.columnHeaders.profileCompleted')"
        class="profile-status-column"
      >
        <template #body="slotProps">
          <TeamUserProfileStatusTag :userId="slotProps.data.id" />
        </template>
      </Column>
      <Column class="actions-column">
        <template #body="slotProps">
          <div class="actions-container">
            <TeamResendInviteButton :user="slotProps.data" />
            <TeamUserInviteButton :user="slotProps.data" label="Invite User" />
            <TeamUserActionsMenu
              :user="slotProps.data"
              @deleteUser="handleDeleteUser"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style lang="less" scoped>
.users-table {
  width: 100%;
}

.users-table :deep(.p-datatable),
.users-table :deep(.p-datatable-table) {
  width: 100%;
  table-layout: fixed;
}

.tooltip {
  color: @color-black;
  padding: 0px 8px;
  cursor: help;
  order: 1;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
  vertical-align: middle;
  min-width: 32px;
}

.user-name {
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.user-name-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.actions-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
}

@media screen and (max-width: 767px) {
  .actions-container {
    gap: 0.25rem;
  }
}

@media screen and (max-width: 767px) {
  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td) {
    flex-wrap: wrap;
    padding: 0.75rem 0.5rem !important;
  }

  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td:first-child) {
    padding-top: 1.5rem !important;
  }

  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td:last-child) {
    padding-bottom: 1.5rem !important;
  }

  .users-table :deep(.p-column-title) {
    margin-bottom: 10px;
    width: 100%;
  }

  .users-table :deep(.p-column-title:empty) {
    margin-bottom: 0;
  }
}

@media screen and (max-width: 960px) {
  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.5rem 1rem !important;
  }

  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td:first-child) {
    padding-top: 1rem !important;
  }

  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td:last-child) {
    padding-bottom: 1rem !important;
  }
}

@media screen and (min-width: 961px) {
  .users-table :deep(.p-datatable .p-datatable-thead > tr > th) {
    padding: 0.75rem 0 0.75rem 1.5rem;
  }

  .users-table :deep(.p-datatable .p-datatable-thead > tr > th:last-child) {
    padding-right: 1.5rem;
  }

  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem 0 0.75rem 1.5rem;
  }

  .users-table :deep(.p-datatable .p-datatable-tbody > tr > td:last-child) {
    padding-right: 1.5rem;
  }

  .users-table :deep(.name-column) {
    width: 225px;
  }

  .users-table :deep(.email-column) {
    width: 225px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .users-table :deep(.role-column) {
    width: 20%;
    max-width: 200px;
  }

  .users-table :deep(.profile-status-column) {
    width: 20%;
    max-width: 200px;
  }

  .users-table :deep(.actions-column) {
    width: 25%;
    max-width: 250px;
  }
}
</style>
